import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/auth-context";
import { createTakeRecorder } from "../native/take-recorder";
import { CAPTURES_ABANDONED_EVENT, createAbandonedCapturesStore } from "./abandoned-captures";
import { fetchCaptures } from "./fetch";
import {
  createPendingRecordingStore,
  PENDING_RECORDING_KEY,
  recoverColdRecording,
  SELECTED_PROJECT_KEY,
} from "./pending-recording";
import { capacitorPreferencesAdapter } from "./preferences-adapter";
import { fetchOwnRecentCaptures, refreshRecordingsToFinish } from "./recordings-to-finish";
import { type ResyncFailure, resyncPendingCaptures } from "./resync-pending";
import { makeSingleFlight } from "./single-flight";
import { createCaptureStorage } from "./storage";
import { syncCaptureToServer } from "./sync-capture";
import { createTakeRunner, TAKES_CHANGED_EVENT } from "./take-runner";
import {
  createProductionRunnerDeps,
  REQUEST_TAKES_RUN_EVENT,
  sharedTakeOverrides,
} from "./take-runtime";
import { recordSyncFailure, refusedTakeFailures } from "./take-sync-failure";

// Filet global « aucune capture ne reste en rade », déclenché au lancement,
// **au retour du réseau** (`online`), **au retour au 1er plan** et à la
// demande d'un écran (`REQUEST_TAKES_RUN_EVENT` : projet choisi pour une
// prise, enregistrement retrouvé, restauration depuis la corbeille). Un seul
// passage à la fois, dans cet ordre :
//   1. Audio FINALISÉ de la trace `pending-recording` (un `stopRecording()` a
//      réussi puis la sauvegarde a échoué, 2026-08-17) : rattaché comme avant.
//      Sans projet résoluble, la trace reste — l'accueil le propose alors
//      (« Enregistrement retrouvé », étape 4).
//   2. Exécuteur des prises (anti-perte phase 1) : fusion à refaire, capture à
//      créer, confirmation puis libération, dernier recours serveur, corbeille,
//      reprise d'une rencontre interrompue (écran de capture).
//   3. Re-synchro de ce qui reste en local non envoyé ; si une capture est
//      partie, nouveau passage de l'exécuteur (sa prise suit aussitôt).
//   4. En ligne seulement : repérage des enregistrements à terminer, publié
//      pour la carte d'accueil.
// Jamais sans session, jamais sur l'écran de capture. Best-effort : chaque
// étape rattrape ses erreurs, le prochain déclencheur rejoue.
export function useResyncPendingCaptures(): void {
  const { client, user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    // Sans session, la RLS rend 0 ligne SANS erreur : une prise confiée au
    // serveur serait lue « capture supprimée » et partirait à la corbeille.
    if (!user) return;
    const storage = createCaptureStorage(capacitorPreferencesAdapter);
    const abandonedStore = createAbandonedCapturesStore(capacitorPreferencesAdapter);
    // La trace peut tenir PLUSIEURS audios en attente : on n'acquitte que
    // l'entrée du fichier rattaché, jamais la clé entière.
    const pendingStore = createPendingRecordingStore(capacitorPreferencesAdapter);
    const recorder = createTakeRecorder();

    // L'écran de capture, s'il est ouvert, traite lui-même la prise qu'il vient
    // d'arrêter : l'exécuteur et lui traiteraient le même `merged` en même temps.
    const onCaptureScreen = () => window.location.pathname.startsWith("/capture");
    const takesAllowed = () => Capacitor.isNativePlatform() && !onCaptureScreen();

    const runner = createTakeRunner(
      createProductionRunnerDeps({
        client,
        user: { id: user.id, email: user.email },
        recorder,
        // Rencontre interrompue : l'écran de capture propose de la reprendre
        // (ou de la sauver telle quelle). Jamais s'il est déjà ouvert.
        offerResume: () => {
          if (!onCaptureScreen()) navigate("/capture?type=rencontre&resume=1");
        },
      }),
    );

    // 1. Audio finalisé de la trace (seule source : le natif ne rend plus de
    //    « récupérable » depuis les prises — tout passe par `Takes/`).
    const attachPendingFinalized = async () => {
      if (!takesAllowed()) return;
      try {
        await recoverColdRecording({
          getRecoverable: async () => null,
          readPendingMetaRaw: () => capacitorPreferencesAdapter.get(PENDING_RECORDING_KEY),
          fallbackProjectId: () => localStorage.getItem(SELECTED_PROJECT_KEY),
          saveCapture: (c) => storage.save(c),
          clearNativeRecoverable: async () => {},
          clearPendingMeta: (savedUri) => pendingStore.release(savedUri),
          newId: () => crypto.randomUUID(),
          userId: user.id,
          nowMs: Date.now(),
        });
      } catch {
        // best-effort : le prochain lancement / retour réseau re-tentera.
      }
    };

    // 2. Exécuteur des prises. Puis, sur une lecture RÉUSSIE, le magasin des
    //    choix note les prises absentes ; un choix n'est oublié qu'absente sur
    //    deux passages, jamais un choix « compléter » (C25) — celui-là part à
    //    la libération (voir `prune`).
    const runTakes = async () => {
      if (!takesAllowed()) return;
      await runner.runAll();
      window.dispatchEvent(new Event(TAKES_CHANGED_EVENT));
      try {
        const { takes } = await recorder.listTakes();
        await sharedTakeOverrides().prune(new Set(takes.map((t) => t.takeId)));
      } catch {
        // Liste illisible : rien n'est oublié.
      }
    };

    // 4. Enregistrements à terminer (comparés aux captures du serveur).
    const findRecordingsToFinish = async () => {
      if (!takesAllowed() || !navigator.onLine) return;
      try {
        await refreshRecordingsToFinish({
          listTakes: () => recorder.listTakes(),
          fetchCaptures: () => fetchOwnRecentCaptures(client, user.id, Date.now()),
          pending: () => pendingStore.list(),
          nowMs: Date.now(),
        });
      } catch (err) {
        console.warn("[resync] repérage des enregistrements à terminer interrompu", err);
      }
    };

    const resync = () =>
      resyncPendingCaptures(
        storage,
        async () => {
          const r = await fetchCaptures(client);
          return r.ok ? new Set(r.data.map((c) => c.id)) : null;
        },
        async (capture) => {
          // Le filet ne fait QU'uploader. L'analyse (transcription + extraction)
          // est désormais à la demande : une capture re-synchronisée se repose en
          // `captured` (statut posé à l'insert) et n'est PAS transcrite
          // automatiquement. L'usager la lance depuis l'écran de fin ou /captures.
          return await syncCaptureToServer({
            client,
            capture,
            user: { id: user.id, email: user.email },
            supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
            anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          });
        },
      );

    // 3 bis. Refus d'envoi d'une capture de prise vus par le filet (C20, R77) :
    //    même classement et même inscription que l'écran de capture — la
    //    prise passe en « Problème signalé », l'exécuteur rappelle le
    //    signalement. Panne réseau ou session expirée : rien de plus. Jamais
    //    sur l'écran de capture : il classe lui-même la prise qu'il suit.
    const recordRefusals = async (failures: ResyncFailure[]): Promise<number> => {
      if (failures.length === 0 || !takesAllowed()) return 0;
      try {
        const { takes } = await recorder.listTakes();
        const refused = refusedTakeFailures(failures, takes, navigator.onLine);
        for (const r of refused) await recordSyncFailure(recorder, r.takeId, "problem", r.detail);
        return refused.length;
      } catch {
        return 0;
      }
    };

    const pass = async () => {
      await attachPendingFinalized();
      await runTakes();
      const result = await resync();
      const refused = await recordRefusals(result.failures);
      // Une capture vient d'arriver sur le serveur : sa prise (`merged`, en
      // attente de cette synchro) est confirmée et libérée MAINTENANT. Sinon
      // elle resterait `merged` jusqu'au prochain déclencheur — et une
      // suppression de la capture entre-temps la ferait recréer (ligne lue
      // absente ⇒ `create_capture`). Un refus vient d'être inscrit :
      // l'exécuteur prévient l'équipe MAINTENANT, et l'accueil se met à jour.
      if (result.resynced > 0 || refused > 0) await runTakes();
      // Observabilité : un échec de re-synchro n'est plus silencieux. On logge
      // un récap (id + cause) → on saura POURQUOI une capture reste coincée
      // (auth, réseau, taille, RLS…) au lieu d'un compteur muet.
      if (result.failed > 0) {
        console.warn(
          `[resync] ${result.resynced} ok, ${result.failed} en échec : ${result.failures
            .map((f) => `${f.id} (${f.error})`)
            .join("; ")}`,
        );
      }
      // Captures ABANDONNÉES : audio local définitivement introuvable. Elles
      // viennent d'être RETIRÉES du buffer (fini la boucle de réessai + le spam
      // de log à chaque 1er plan). On les JOURNALISE (capture complète → notice
      // usager sur l'accueil + réversibilité d'un faux positif), puis on signale
      // la bannière déjà montée qu'il y a du neuf à afficher.
      if (result.abandoned.length > 0) {
        const nowIso = new Date().toISOString();
        for (const a of result.abandoned) {
          await abandonedStore.add(a.capture, a.error, nowIso);
        }
        window.dispatchEvent(new Event(CAPTURES_ABANDONED_EVENT));
        console.warn(
          `[resync] ${result.abandoned.length} capture(s) abandonnée(s) (audio introuvable, retirée(s)) : ${result.abandoned
            .map((a) => `${a.capture.id} (${a.error})`)
            .join("; ")}`,
        );
      }
      await findRecordingsToFinish();
    };

    // Garde single-flight : le filet est déclenché au montage + sur `online` +
    // sur CHAQUE `appStateChange` actif. Sans verrou, deux passages concurrents
    // ré-uploadaient la même capture (double INSERT → collision PK 23505). Un
    // déclencheur automatique concurrent est ignoré (le prochain rejouera) ;
    // une DEMANDE d'écran, elle, est rejouée juste après le passage en cours —
    // sinon une prise dont le projet vient d'être choisi attendrait le
    // prochain retour au premier plan.
    const singleFlight = makeSingleFlight();
    let rerunRequested = false;
    const trigger = (fromRequest: boolean) =>
      void singleFlight(pass)
        .then((ran) => {
          if (!ran) {
            if (fromRequest) rerunRequested = true;
            return;
          }
          if (rerunRequested) {
            rerunRequested = false;
            trigger(true);
          }
        })
        .catch(() => {});

    trigger(false);
    const onOnline = () => trigger(false);
    const onRequest = () => trigger(true);
    window.addEventListener("online", onOnline);
    window.addEventListener(REQUEST_TAKES_RUN_EVENT, onRequest);
    const appStateHandle = App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) trigger(false);
    });
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener(REQUEST_TAKES_RUN_EVENT, onRequest);
      void appStateHandle.then((h) => h.remove());
    };
  }, [client, user, navigate]);
}
